import { apiRequest } from './client';

/**
 * A5b.5 — data sources & API keys client.
 *
 * Reads/list/revoke a tenant's ingest API keys and its live `data_points`. The
 * plaintext key is returned **once** at creation and never stored server-side
 * (only its hash), so it exists only in the create response.
 */

export type ApiKeyRow = {
  id: string;
  name: string;
  scopes: unknown;
  created: string | null;
};

export type CreatedApiKey = {
  api_key_id: string;
  key: string;
  scopes: { keys: string[] };
};

export type DataKeyRow = {
  key: string;
  value: unknown;
  version: number;
  last_seen: string | null;
};

/** The tenant's API keys — never the plaintext key or its hash. */
export async function listApiKeys(): Promise<ApiKeyRow[]> {
  const body = await apiRequest<{ keys: ApiKeyRow[] }>('/api-keys');
  return body.keys ?? [];
}

/**
 * Create an ingest key. `keys` scopes it to specific `data_point` keys (empty =
 * any key), enforced by the server at ingest time.
 */
export async function createApiKey(input: { name: string; keys?: string[] }): Promise<CreatedApiKey> {
  return apiRequest('/api-keys', { method: 'POST', body: input });
}

/** Revoke (delete) an API key — ingest with it is rejected immediately after. */
export async function revokeApiKey(id: string): Promise<{ ok: boolean }> {
  return apiRequest(`/api-keys/${encodeURIComponent(id)}/revoke`, { method: 'POST' });
}

/** The tenant's live data points (value + version + last seen). */
export async function listDataKeys(): Promise<DataKeyRow[]> {
  const body = await apiRequest<{ keys: DataKeyRow[] }>('/data-keys');
  return body.keys ?? [];
}
